import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { useFetchQuantifyFormulas } from '@modules/quantify/hooks/useFetchQuantifyFormulas';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { useEffect } from 'react';

interface useGetQuantifyFormulaDisplayNameParams {
  formulaID?: string;
  shouldIncludeGroupByName?: boolean;
}

export function useGetQuantifyFormulaDisplayName({
  formulaID: formulaIDProp,
  shouldIncludeGroupByName = false,
}: useGetQuantifyFormulaDisplayNameParams = {}) {
  const [fetchQuantifyFormulas, { formulas, isLoading }] =
    useFetchQuantifyFormulas({ fetchPolicy: 'cache-first' });
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const groupByDisplayName = useQuantifyStore(({ query }) =>
    getFormattedDisplayNameForEqlField({
      eqlField: query.groupBy,
      shouldRemoveFileUploadPrefix,
      isUnifiedFieldEnabled,
    }),
  );

  const groupByField = useQuantifyStore(({ query }) => query.groupBy?.field);
  const formulaID = useQuantifyStore(
    ({ query }) => formulaIDProp ?? query.formulaID,
  );

  useEffect(() => {
    groupByField && fetchQuantifyFormulas(groupByField);
  }, [fetchQuantifyFormulas, groupByField]);

  const displayName = formulas
    ? formulas.find((formula) => formula.eqlFunction === formulaID)?.shortName
    : formulaID;

  return {
    formulaDisplayName: shouldIncludeGroupByName
      ? `${groupByDisplayName} : ${displayName}`
      : displayName,
    isLoading,
  };
}
